import { create } from 'zustand';
import type { ConversationCall, CallSignal } from '../types';

export type LocalCallStatus =
    | 'idle'
    | 'ringing'
    | 'outgoing'
    | 'connecting'
    | 'ongoing'
    | 'ended'
    | 'error';

interface CallState {
    activeCall: ConversationCall | null;
    callStatus: LocalCallStatus;
    connectionState: RTCPeerConnectionState | null;
    isMuted: boolean;
    isVideoEnabled: boolean;
    isSpeakerOn: boolean;
    isLocalVideoLarge: boolean;
    isBackCamera: boolean;
    callDuration: number;
    callError: string | null;
    pendingSignals: CallSignal[];

    setActiveCall: (activeCall: ConversationCall | null) => void;
    setCallStatus: (callStatus: LocalCallStatus) => void;
    setConnectionState: (connectionState: RTCPeerConnectionState | null) => void;
    setCallDuration: (callDuration: number) => void;
    setCallError: (callError: string | null) => void;
    setPendingSignals: (pendingSignals: CallSignal[] | ((prev: CallSignal[]) => CallSignal[])) => void;
    setCallFlags: (
        flags: Partial<
            Pick<
                CallState,
                'isMuted' | 'isVideoEnabled' | 'isSpeakerOn' | 'isLocalVideoLarge' | 'isBackCamera'
            >
        >
    ) => void;
    toggleMute: () => void;
    toggleVideo: () => void;
    toggleSpeaker: () => void;
    toggleLocalVideoSize: () => void;
    toggleBackCamera: () => void;
    clearCallError: () => void;
    resetCallState: () => void;
}

const initialState: Omit<
    CallState,
    | 'setActiveCall'
    | 'setCallStatus'
    | 'setConnectionState'
    | 'setCallDuration'
    | 'setCallError'
    | 'setPendingSignals'
    | 'setCallFlags'
    | 'toggleMute'
    | 'toggleVideo'
    | 'toggleSpeaker'
    | 'toggleLocalVideoSize'
    | 'toggleBackCamera'
    | 'clearCallError'
    | 'resetCallState'
> = {
    activeCall: null,
    callStatus: 'idle',
    connectionState: null,
    isMuted: false,
    isVideoEnabled: false,
    isSpeakerOn: false,
    isLocalVideoLarge: false,
    isBackCamera: false,
    callDuration: 0,
    callError: null,
    pendingSignals: [],
};

export const useCallStore = create<CallState>((set) => ({
    ...initialState,
    setActiveCall: (activeCall) => set({ activeCall }),
    setCallStatus: (callStatus) => set({ callStatus }),
    setConnectionState: (connectionState) => set({ connectionState }),
    setCallDuration: (callDuration) => set({ callDuration }),
    setCallError: (callError) => set({ callError }),
    setPendingSignals: (pendingSignals) =>
        set((state) => ({
            pendingSignals:
                typeof pendingSignals === 'function'
                    ? pendingSignals(state.pendingSignals)
                    : pendingSignals,
        })),
    setCallFlags: (flags) => set(flags),
    toggleMute: () => set((state) => ({ isMuted: !state.isMuted })),
    toggleVideo: () => set((state) => ({ isVideoEnabled: !state.isVideoEnabled })),
    toggleSpeaker: () => set((state) => ({ isSpeakerOn: !state.isSpeakerOn })),
    toggleLocalVideoSize: () => set((state) => ({ isLocalVideoLarge: !state.isLocalVideoLarge })),
    toggleBackCamera: () => set((state) => ({ isBackCamera: !state.isBackCamera })),
    clearCallError: () => set({ callError: null }),
    resetCallState: () => set(initialState),
}));
